import { useState, type ReactNode } from 'react';
import { centavos, formatMoney, parseMoney, type Centavos } from '@abastece/shared';
import type { Cliente, ListaPrecio } from '@abastece/core';
import { etiquetaListaPrecio } from '../lib/catalogo.js';
import { formatearTelefono, revisarTelefono, telefonoAceptable, telefonoParaGuardar } from '../lib/telefono.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { TomarFotoCedula } from './FotoCedula.js';
import { Ico } from './Iconos.js';

/** 0 = domingo, matching Date.getDay() so "who do we visit today" is an index and not a lookup. */
export const DIAS: Clave[] = ['dia.0', 'dia.1', 'dia.2', 'dia.3', 'dia.4', 'dia.5', 'dia.6'];

export interface BorradorCliente {
  nombre: string;
  tipo: 'mostrador' | 'mayorista';
  telefono: string;
  direccion: string;
  listaId: string;
  diaVisita: string;
  notas: string;
  /** Fiado limit as typed. Empty: every fiado takes the owner. */
  limite: string;
  /** What they already owed before the shop kept fiado here, as typed. Empty: nothing. */
  saldoAnterior: string;
  /** The cédula's number as typed, and the name of the photo of the card. */
  cedula: string;
  fotoCedula: string | null;
}

export const CLIENTE_VACIO: BorradorCliente = {
  nombre: '',
  tipo: 'mayorista',
  telefono: '',
  direccion: '',
  listaId: '',
  diaVisita: '',
  notas: '',
  limite: '',
  saldoAnterior: '',
  cedula: '',
  fotoCedula: null,
};

/** Somebody who walked up to the counter: a neighbour, not a shop on the route. */
export const CLIENTE_MOSTRADOR: BorradorCliente = { ...CLIENTE_VACIO, tipo: 'mostrador' };

export function borradorDe(c: Cliente): BorradorCliente {
  return {
    nombre: c.nombre,
    tipo: c.tipo,
    telefono: c.telefono ?? '',
    direccion: c.direccion ?? '',
    listaId: c.listaId ?? '',
    diaVisita: c.diaVisita === null ? '' : String(c.diaVisita),
    notas: c.notas ?? '',
    limite:
      c.limiteCredito === null || c.limiteCredito === undefined
        ? ''
        : formatMoney(c.limiteCredito, { symbol: false }).replace(/,/g, ''),
    saldoAnterior: c.saldoInicial ? formatMoney(c.saldoInicial, { symbol: false }).replace(/,/g, '') : '',
    cedula: c.cedula ?? '',
    fotoCedula: c.fotoCedula ?? null,
  };
}

/**
 * The event's fields, from what was typed.
 *
 * The limit goes in only for somebody who may set it, so a cashier editing a phone number
 * cannot quietly clear the owner's decision. The cédula goes in when this form showed it: to
 * anybody adding a client, since whoever is at the counter is the one holding the card, but on
 * an existing client only to the owner, who is the only one who sees it afterwards.
 */
export function camposDe(
  b: BorradorCliente,
  incluir: { limite: boolean; lista: boolean; cedula: boolean },
) {
  const limite = b.limite.trim() === '' ? null : parseMoney(b.limite);
  return {
    nombre: b.nombre.trim(),
    tipo: b.tipo,
    // The form only saves a good number, so this is the number as the shop writes it.
    telefono: telefonoParaGuardar(b.telefono),
    direccion: b.direccion.trim() || null,
    // The price list, like the limit, only from somebody who may set it (`elegir_lista`).
    ...(incluir.lista ? { listaId: b.listaId || null } : {}),
    diaVisita: b.diaVisita === '' ? null : Number(b.diaVisita),
    notas: b.notas.trim() || null,
    ...(incluir.limite ? { limiteCredito: limite === null || limite <= 0 ? null : centavos(limite) } : {}),
    ...(incluir.cedula
      ? { cedula: b.cedula.trim().toUpperCase() || null, fotoCedula: b.fotoCedula }
      : {}),
  };
}

/** Whether what is typed in the limit is something the form can save. */
const limiteValido = (texto: string) => texto.trim() === '' || (parseMoney(texto) ?? -1) >= 0;

/**
 * What they owed from before, from what was typed: empty is nothing, and null is something the
 * form cannot save. It is its own event (`cliente.saldo_inicial`), sent after the client's and
 * only when it changed, by whoever may give fiado.
 */
export function saldoAnteriorDe(b: BorradorCliente): Centavos | null {
  if (b.saldoAnterior.trim() === '') return 0 as Centavos;
  const monto = parseMoney(b.saldoAnterior);
  return monto === null || monto < 0 ? null : monto;
}

export function FormularioCliente({
  b,
  setB,
  listas,
  puedeDarCredito,
  puedeElegirLista,
  conCedula,
  compacto = false,
  enHoja = false,
  telefonoObligatorio = false,
  yaEsta,
  onElegir,
  onGuardar,
  extra,
}: {
  b: BorradorCliente;
  setB: (b: BorradorCliente) => void;
  listas: readonly ListaPrecio[];
  /** The owner: sees and sets the fiado limit. */
  puedeDarCredito: boolean;
  /** The owner: gives the client a price list. `camposDe` has to be told the same. */
  puedeElegirLista: boolean;
  /** Shows the cédula's number and photo. `camposDe` has to be told the same. */
  conCedula: boolean;
  /**
   * At the counter: who they are and how to reach them, and nothing about the route — the
   * price list, the kind of client and the visiting day can be set later from «Clientes».
   */
  compacto?: boolean;
  /** The whole form in a sheet, which is card enough: a new order's client (`ui/TomarPedido.tsx`). */
  enHoja?: boolean;
  /** At the counter the phone is how the shop will collect, so a client is not saved without one. */
  telefonoObligatorio?: boolean;
  /**
   * An active client already called what is typed (`clienteLlamado`): said under the name, and not
   * saved while it is, since two of one client split their fiado and their orders in two. Where
   * there is an order to pick them for, `onElegir` picks that one instead (the owner, 2026-10-04).
   */
  yaEsta?: { id: string; nombre: string } | undefined;
  onElegir?: (clienteId: string) => void;
  onGuardar: () => void;
  /** A second button beside «Guardar», such as deactivating the client. */
  extra?: ReactNode;
}) {
  const t = useT();
  const listo =
    b.nombre.trim() !== '' &&
    limiteValido(b.limite) &&
    saldoAnteriorDe(b) !== null &&
    telefonoAceptable(b.telefono, telefonoObligatorio) &&
    !yaEsta;
  return (
    // At the counter it is the whole of a sheet, which is card enough.
    <section className={`${compacto || enHoja ? '' : 'tarjeta mb-4'} flex flex-col gap-4`}>
      <div data-guia="cliente-nombre">
        <Campo
          maxLength={120}
          etiqueta={t('comun.nombre')}
          value={b.nombre}
          onChange={(e) => setB({ ...b, nombre: e.target.value })}
          autoFocus
          autoComplete="off"
          error={yaEsta ? t('clientes.yaExiste', { nombre: yaEsta.nombre }) : null}
        />
      </div>
      {yaEsta && onElegir && (
        <button type="button" className="btn-secundario" onClick={() => onElegir(yaEsta.id)} data-cliente-elegir-ese>
          <Ico icono="listo" />
          {t('clientes.elegirEse', { nombre: yaEsta.nombre })}
        </button>
      )}
      {!compacto && (
        <>
          <div data-guia="cliente-tipo">
            <Selector
              etiqueta={t('clientes.tipo')}
              value={b.tipo}
              onChange={(e) => setB({ ...b, tipo: e.target.value as BorradorCliente['tipo'] })}
            >
              <option value="mostrador">{t('clientes.mostrador')}</option>
              <option value="mayorista">{t('clientes.mayorista')}</option>
            </Selector>
          </div>
          <div data-guia="cliente-lista">
            <Selector
              etiqueta={t('clientes.listaPrecios')}
              ayuda={t(puedeElegirLista ? 'clientes.ayudaLista' : 'clientes.listaDelDueno')}
              value={b.listaId}
              onChange={(e) => setB({ ...b, listaId: e.target.value })}
              disabled={!puedeElegirLista}
              className={puedeElegirLista ? '' : 'text-slate-400'}
            >
              <option value="">{t('clientes.laPredeterminada')}</option>
              {listas.map((l) => (
                <option key={l.id} value={l.id}>
                  {etiquetaListaPrecio(l)}
                </option>
              ))}
            </Selector>
          </div>
        </>
      )}
      {puedeDarCredito && !compacto && (
        <>
          <CampoLimite b={b} setB={setB} />
          <Campo
            etiqueta={t('clientes.saldoAnterior')}
            ayuda={t('clientes.ayudaSaldoAnterior')}
            value={b.saldoAnterior}
            onChange={(e) => setB({ ...b, saldoAnterior: e.target.value })}
            inputMode="decimal"
            className="tabular"
            placeholder="0.00"
            error={saldoAnteriorDe(b) === null ? t('clientes.saldoAnteriorInvalido') : undefined}
            autoComplete="off"
          />
        </>
      )}
      <CampoTelefono
        valor={b.telefono}
        onCambio={(telefono) => setB({ ...b, telefono })}
        obligatorio={telefonoObligatorio}
      />
      <Campo
        maxLength={300}
        etiqueta={t('comun.direccion')}
        ayuda={t('clientes.ayudaDireccion')}
        value={b.direccion}
        onChange={(e) => setB({ ...b, direccion: e.target.value })}
        autoComplete="off"
      />
      {conCedula && (
        <>
          <Campo
            maxLength={30}
            etiqueta={t('clientes.cedula')}
            ayuda={t('clientes.ayudaCedula')}
            value={b.cedula}
            onChange={(e) => setB({ ...b, cedula: e.target.value })}
            placeholder={t('clientes.ejemploCedula')}
            className="tabular uppercase"
            autoComplete="off"
          />
          <TomarFotoCedula huella={b.fotoCedula} onCambio={(fotoCedula) => setB({ ...b, fotoCedula })} />
        </>
      )}
      {/* At the counter the limit comes last: it is the owner's decision about who they just met. */}
      {puedeDarCredito && compacto && <CampoLimite b={b} setB={setB} />}
      {!compacto && (
        <>
          <div data-guia="cliente-dia">
            <Selector
              etiqueta={t('clientes.diaVisita')}
              value={b.diaVisita}
              onChange={(e) => setB({ ...b, diaVisita: e.target.value })}
            >
              <option value="">{t('clientes.sinDiaFijo')}</option>
              {DIAS.map((d, i) => (
                <option key={d} value={i}>
                  {t(d)}
                </option>
              ))}
            </Selector>
          </div>
          <Campo
            maxLength={500}
            etiqueta={t('clientes.notas')}
            value={b.notas}
            onChange={(e) => setB({ ...b, notas: e.target.value })}
            autoComplete="off"
          />
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primario flex-1"
          data-guia="cliente-guardar"
          disabled={!listo}
          onClick={onGuardar}
        >
          <Ico icono="guardar" />
          {t('comun.guardar')}
        </button>
        {extra}
      </div>
    </section>
  );
}

/**
 * The phone, typed strictly (`lib/telefono.ts`): digits only, eight of them, grouped as they go
 * in. While it is typed it counts what is missing; once it is left, a number that is short or
 * starts wrong says so in red, and the form does not save until it is right. When it is right,
 * it asks for the one check no rule can make: reading it back to the person it belongs to.
 */
function CampoTelefono({
  valor,
  onCambio,
  obligatorio,
}: {
  valor: string;
  onCambio: (valor: string) => void;
  obligatorio: boolean;
}) {
  const t = useT();
  const [dejado, setDejado] = useState(false);
  const estado = revisarTelefono(valor);
  const error =
    estado.tipo === 'invalido'
      ? t(estado.motivo === 'prefijo' ? 'clientes.telefonoPrefijo' : 'clientes.telefonoLargo')
      : dejado && estado.tipo === 'incompleto'
        ? estado.extranjero
          ? t('clientes.telefonoExtranjeroCorto')
          : t('clientes.telefonoIncompleto', { n: estado.faltan })
        : dejado && estado.tipo === 'vacio' && obligatorio
          ? t('clientes.telefonoFalta')
          : undefined;
  const ayuda =
    estado.tipo === 'valido'
      ? t('clientes.telefonoConfirmar')
      : estado.tipo === 'incompleto'
        ? t('clientes.faltanDigitos', { n: estado.faltan })
        : t(obligatorio ? 'clientes.ayudaTelefonoObligatorio' : 'clientes.ayudaTelefono');
  return (
    <Campo
      etiqueta={t('comun.telefono')}
      value={valor}
      onChange={(e) => onCambio(formatearTelefono(e.target.value))}
      onBlur={() => setDejado(true)}
      inputMode="tel"
        maxLength={40}
      autoComplete="off"
      placeholder="8888 8888"
      className="tabular text-lg tracking-wide"
      error={error}
      ayuda={ayuda}
    />
  );
}

function CampoLimite({ b, setB }: { b: BorradorCliente; setB: (b: BorradorCliente) => void }) {
  const t = useT();
  return (
    <Campo
      etiqueta={t('fiado.limite')}
      ayuda={t('clientes.ayudaLimite')}
      value={b.limite}
      onChange={(e) => setB({ ...b, limite: e.target.value })}
      inputMode="decimal"
      className="tabular"
      placeholder={t('fiado.sinFiado')}
      error={limiteValido(b.limite) ? undefined : t('clientes.limiteInvalido')}
      autoComplete="off"
    />
  );
}
